import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, is429, retryAfterSec } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, ShieldCheck, Clock, Lock, Users, Calendar, GraduationCap, InfinityIcon, Warehouse, DollarSign, RotateCcw, CheckCircle2 } from "lucide-react";
import { SMS_MARKETING_LABEL, SMS_TRANSACTIONAL_LABEL, SMS_PHONE_DISCLOSURE } from "@/config/smsConsent";

const STEPS = [
  { key: "contact",   label: "Contact" },
  { key: "business",  label: "Business Stage" },
  { key: "goals",     label: "Goals" },
  { key: "readiness", label: "Readiness" },
  { key: "review",    label: "Review & Consent" },
];

const initial = {
  full_name: "", email: "", mobile_phone: "", city: "", state: "", country: "", preferred_contact: "Email",
  address: "", zip_code: "",
  owns_business: "No", business_name: "", website: "", social_links: "", years_operating: "", revenue_range: "", units_owned: "", trailer_vehicle: "", team_size: "",
  goals_12_months: "", biggest_challenge: "", why_interested: "", success_definition: "", start_when: "", weekly_time: "",
  ready_to_invest: "Yes", open_to_payment_plan: "Open to discussion", can_attend_training: "Yes", willing_to_complete: "Yes", prepared_additional_costs: "Yes", funding_plan: "",
  consent_no_guarantee_acceptance: false, consent_no_guarantee_results: false, consent_contact: false, consent_accuracy: false, consent_privacy_terms: false,
  // A2P 10DLC — SMS consents are independent, optional, unchecked by default.
  // The phone number alone is NOT SMS consent. See /app/frontend/src/config/smsConsent.js
  sms_marketing_consent: false, sms_transactional_consent: false,
  sms_consent_language_marketing: "", sms_consent_language_transactional: "",
  hp_field: "",
};

const AUTOSAVE_KEY = "bcu-application-draft-v4";
const AUTOSAVE_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const SENSITIVE_KEYS = ["hp_field"]; // never persisted; funding_plan hint tells users not to include card/bank numbers

function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <span className="bcu-label">{label}{required && <span className="text-[#F7931E]"> *</span>}</span>
      {children}
      {hint && <span className="mt-2 block text-xs text-[#7A8296]">{hint}</span>}
    </label>
  );
}

export default function Apply() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(() => {
    try {
      const raw = localStorage.getItem(AUTOSAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Expiration check
        if (parsed?.ts && Date.now() - parsed.ts < AUTOSAVE_TTL_MS && parsed.data) {
          return { ...initial, ...parsed.data, hp_field: "" };
        }
        // Legacy or expired — drop it
        localStorage.removeItem(AUTOSAVE_KEY);
      }
    } catch { /* ignore */ }
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const submittedRef = useRef(false);
  const flashTimerRef = useRef(null);
  const trackedRef = useRef(""); // email we've already tracked this session
  const autosaveRateLimitedRef = useRef(false);
  const submitBtnRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (submittedRef.current) return; // do not re-persist after submit
    try {
      const persisted = { ...data };
      SENSITIVE_KEYS.forEach((k) => { delete persisted[k]; });
      localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ ts: Date.now(), data: persisted }));
      setSavedFlash(true);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setSavedFlash(false), 1400);
    } catch { /* ignore */ }
  }, [data]);

  // Track an abandoned-application draft as soon as the visitor supplies name + a valid email.
  // The backend uses this to send a single 48-hour reminder if they don't submit.
  useEffect(() => {
    if (submittedRef.current) return;
    const email = (data.email || "").trim().toLowerCase();
    const name = (data.full_name || "").trim();
    if (!email || !name) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (trackedRef.current === email) return;
    const t = setTimeout(() => {
      trackedRef.current = email;
      api.post("/application-drafts/track", { full_name: name, email }).catch((err) => {
        if (is429(err)) {
          // Backend is throttling the lightweight draft tracker. Show a single, quiet
          // notice (deduplicated by toast id) and stop retrying this session so we
          // never spam the visitor with repeated toasts on every keystroke.
          if (!autosaveRateLimitedRef.current) {
            autosaveRateLimitedRef.current = true;
            toast.message("Too many attempts were received. Please wait a moment and try again.", { id: "rl-autosave", duration: 6000 });
          }
          // Keep trackedRef pinned so we do not immediately re-hit the endpoint.
        } else {
          trackedRef.current = ""; // retry on next change
        }
      });
    }, 1200);
    return () => clearTimeout(t);
  }, [data.email, data.full_name]);

  const resetForm = () => {
    try { localStorage.removeItem(AUTOSAVE_KEY); } catch { /* ignore */ }
    setData(initial);
    setStep(0);
    setConfirmReset(false);
    toast.success("Application draft cleared.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : (e?.target?.value ?? e);
    setData((d) => ({ ...d, [k]: v }));
  };

  const validateStep = () => {
    if (step === 0) {
      if (!data.full_name || !data.email || !data.mobile_phone) return "Please complete name, email and mobile phone.";
    }
    if (step === 2) {
      if (!data.goals_12_months || !data.biggest_challenge || !data.why_interested) return "Please answer the required goal questions.";
    }
    if (step === 3) {
      if (!data.ready_to_invest || !data.can_attend_training || !data.willing_to_complete || !data.prepared_additional_costs) return "Please answer all readiness questions.";
    }
    if (step === 4) {
      const consents = [data.consent_no_guarantee_acceptance, data.consent_no_guarantee_results, data.consent_contact, data.consent_accuracy, data.consent_privacy_terms];
      if (!consents.every(Boolean)) return "Please accept all consent items to submit.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    if (submitting || submittedRef.current) return; // prevent double submit
    setSubmitting(true);
    // Clear any lingering cooldown from a previous 429 before this fresh attempt.
    if (cooldownTimerRef.current) { clearTimeout(cooldownTimerRef.current); cooldownTimerRef.current = null; }
    try {
      const { data: resp } = await api.post("/applications", data);
      submittedRef.current = true;
      try { localStorage.removeItem(AUTOSAVE_KEY); } catch { /* ignore */ }
      // Preserve the newly-created application id so the confirmation page
      // can attach the applicant's payment preference to the correct record.
      // Sent via nav state (primary) with sessionStorage as a fallback in
      // case the visitor hard-refreshes the confirmation page.
      const newAppId = resp && resp.id ? String(resp.id) : "";
      if (newAppId) {
        try { sessionStorage.setItem("bcu_last_application_id", newAppId); } catch { /* ignore */ }
      }
      // Meta Pixel — fire the standard `SubmitApplication` event exactly once
      // per successful backend submission. Guarded by sessionStorage keyed on
      // the newly-issued application_id so the event never fires again if the
      // applicant refreshes the confirmation page or navigates back.
      try {
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          const fbqKey = newAppId ? `bcu_fbq_submitapp:${newAppId}` : "bcu_fbq_submitapp:noid";
          let alreadyFired = false;
          try { alreadyFired = sessionStorage.getItem(fbqKey) === "1"; } catch { /* ignore */ }
          if (!alreadyFired) {
            window.fbq("track", "SubmitApplication");
            try { sessionStorage.setItem(fbqKey, "1"); } catch { /* ignore */ }
          }
        }
      } catch { /* never let analytics break the flow */ }
      navigate("/application-received", { state: { applicationId: newAppId } });
    } catch (e) {
      if (is429(e)) {
        const sec = retryAfterSec(e);
        toast.error("We received too many submission attempts. Your progress is still saved. Please wait a few minutes and try again.", { id: "rl-apply", duration: 8000 });
        // Keep the submit button disabled briefly. Autosaved data is untouched.
        if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = setTimeout(() => {
          setSubmitting(false);
          // Return keyboard focus to the submit button so a screen-reader / keyboard
          // user can retry without hunting for the control.
          try { submitBtnRef.current?.focus(); } catch { /* ignore */ }
        }, sec * 1000);
        return; // do not clear `submitting` in the finally block below
      }
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(String(msg));
    } finally {
      // Only clear submitting if we're not currently in a rate-limit cooldown.
      if (!cooldownTimerRef.current) setSubmitting(false);
    }
  };

  useEffect(() => () => { if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current); }, []);

  const progress = ((step + 1) / STEPS.length) * 100;

  const OVERVIEW = [
    { icon: Calendar,       t: "12 Weeks",         s: "Live Mentorship" },
    { icon: GraduationCap,  t: "3 Phases",         s: "Foundation, Build, Scale" },
    { icon: Warehouse,      t: "Live Warehouse",   s: "Hands-On Training" },
    { icon: ShieldCheck,    t: "Done-With-You",    s: "Build Day" },
    { icon: InfinityIcon,   t: "Lifetime",         s: "Portal Access" },
    { icon: Users,          t: "Private",          s: "Community" },
  ];

  return (
    <div data-testid="apply-page" className="sect-navy pt-28 pb-24 min-h-screen">
      <div className="bcu-container">
        <div className="max-w-3xl">
          <p className="bcu-eyebrow">Application</p>
          <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-display leading-[1.02]">Apply for <span className="text-[#F7931E]">Bounce City University</span></h1>
          <p className="mt-4 text-[#C7CFDD] text-[17px]">Take the first step toward building a profitable inflatable rental business. Our enrollment team reviews every application privately.</p>
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
          {/* LEFT — form */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {/* Steps */}
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              {STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 md:gap-3">
                  <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-bold font-display ${i <= step ? "border-[#F7931E] bg-[#F7931E] text-black" : "border-[rgba(255,255,255,0.14)] text-[#888888]"}`}>{i + 1}</span>
                  <span className={`text-[12px] md:text-[13px] font-bold font-display uppercase tracking-[0.1em] ${i === step ? "text-white" : "text-[#7A8296]"}`}>{s.label}</span>
                  {i < STEPS.length - 1 && <span className="w-4 md:w-8 h-px bg-[rgba(255,255,255,0.14)]" />}
                </div>
              ))}
            </div>
            <div className="mt-4 h-1 bg-[rgba(255,255,255,0.10)] rounded-sm overflow-hidden">
              <div className="h-full bg-[#F7931E] transition-all duration-500" style={{ width: `${progress}%` }} data-testid="apply-progress" />
            </div>

            {/* Autosave / Start over row */}
            <div className="mt-3 flex items-center justify-between flex-wrap gap-3 text-[12px] text-[#888888]">
              <div data-testid="autosave-indicator" className={`inline-flex items-center gap-2 transition-opacity duration-300 ${savedFlash ? "opacity-100" : "opacity-60"}`} aria-live="polite">
                <CheckCircle2 className={`h-3.5 w-3.5 ${savedFlash ? "text-[#78C800]" : "text-[#7A7A7A]"}`} />                <span>{savedFlash ? "Progress saved" : "Autosaved to this browser (14 days)"}</span>
              </div>
              <button type="button" onClick={() => setConfirmReset(true)} data-testid="apply-start-over" className="inline-flex items-center gap-2 text-[12px] font-medium text-[#B5B5B5] hover:text-[#F7931E] transition-colors">
                <RotateCcw className="h-3.5 w-3.5" /> Start over
              </button>
            </div>

            {confirmReset && (
              <div data-testid="reset-confirm" className="mt-4 border border-[#F7931E]/40 bg-[#0D0D0D] p-4 rounded-md flex items-start justify-between gap-4 flex-wrap">
                <div className="text-[14px] text-white max-w-md">
                  Clear your saved progress and start a fresh application? This cannot be undone.
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={resetForm} data-testid="reset-confirm-yes" className="bcu-btn-primary text-[12px] tracking-[0.10em] uppercase px-4 py-2">Clear draft</button>
                  <button onClick={() => setConfirmReset(false)} data-testid="reset-confirm-no" className="bcu-btn-secondary text-[12px] tracking-[0.10em] uppercase px-4 py-2">Keep editing</button>
                </div>
              </div>
            )}

            <div className="mt-8 bcu-card p-6 md:p-10">
              <input type="text" name="hp_field" value={data.hp_field} onChange={set("hp_field")} className="hidden" tabIndex={-1} autoComplete="off" />

              {step === 0 && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Step 01</div>
                    <h2 className="font-display text-2xl md:text-3xl mt-1">Contact Information</h2>
                  </div>
                  <p className="text-sm text-[#A9B1C1]">Just the basics — enough for our enrollment team to reach you. Full address is collected only after acceptance.</p>
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <Field label="Full name" required><input data-testid="apply-full-name" className="bcu-input" value={data.full_name} onChange={set("full_name")} autoComplete="name" /></Field>
                    <Field label="Email" required><input data-testid="apply-email" type="email" className="bcu-input" value={data.email} onChange={set("email")} autoComplete="email" inputMode="email" /></Field>
                    <Field label="Mobile phone" required><input data-testid="apply-mobile" type="tel" className="bcu-input" value={data.mobile_phone} onChange={set("mobile_phone")} autoComplete="tel" inputMode="tel" /></Field>
                    <Field label="Preferred method of contact"><select data-testid="apply-preferred-contact" className="bcu-input" value={data.preferred_contact} onChange={set("preferred_contact")}><option>Email</option><option>Phone</option><option>SMS</option></select></Field>
                    <Field label="City"><input className="bcu-input" value={data.city} onChange={set("city")} autoComplete="address-level2" /></Field>
                    <Field label="State / Province"><input className="bcu-input" value={data.state} onChange={set("state")} autoComplete="address-level1" /></Field>
                    <Field label="Country"><input className="bcu-input" value={data.country} onChange={set("country")} autoComplete="country-name" /></Field>
                  </div>

                  {/* A2P 10DLC — independent, optional SMS consents. Placed
                      directly beneath the phone/contact block so it is
                      unmistakable that providing a phone number does NOT
                      enroll the applicant in SMS. Both checkboxes default to
                      unchecked and are never required to submit. */}
                  <div className="mt-2 border border-[rgba(255,255,255,0.10)] rounded-lg p-5 bg-[#0A0A0A]">
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">SMS / Text Message Preferences</div>
                    <p className="mt-2 text-xs text-[#7A8296] leading-relaxed">{SMS_PHONE_DISCLOSURE}</p>
                    <div className="mt-4 space-y-3">
                      <label className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
                        <input
                          type="checkbox"
                          data-testid="apply-sms-transactional"
                          checked={!!data.sms_transactional_consent}
                          onChange={(e) => setData((d) => ({
                            ...d,
                            sms_transactional_consent: e.target.checked,
                            sms_consent_language_transactional: e.target.checked ? SMS_TRANSACTIONAL_LABEL : "",
                          }))}
                          className="mt-1 h-4 w-4 accent-[#F7931E]"
                        />
                        <span className="text-sm text-[#C7CFDD] leading-relaxed">{SMS_TRANSACTIONAL_LABEL}</span>
                      </label>
                      <label className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
                        <input
                          type="checkbox"
                          data-testid="apply-sms-marketing"
                          checked={!!data.sms_marketing_consent}
                          onChange={(e) => setData((d) => ({
                            ...d,
                            sms_marketing_consent: e.target.checked,
                            sms_consent_language_marketing: e.target.checked ? SMS_MARKETING_LABEL : "",
                          }))}
                          className="mt-1 h-4 w-4 accent-[#F7931E]"
                        />
                        <span className="text-sm text-[#C7CFDD] leading-relaxed">{SMS_MARKETING_LABEL}</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#168CFF] font-bold">Step 02</div>
                    <h2 className="font-display text-2xl md:text-3xl mt-1">Business Stage</h2>
                  </div>
                  <p className="text-sm text-[#A9B1C1]">A quick snapshot of where you are today. Optional fields are fine to skip.</p>
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <Field label="Do you currently own an inflatable rental business?"><select className="bcu-input" value={data.owns_business} onChange={set("owns_business")}><option>No</option><option>Yes</option></select></Field>
                    <Field label="Business name"><input className="bcu-input" value={data.business_name} onChange={set("business_name")} /></Field>
                    <Field label="Website"><input className="bcu-input" value={data.website} onChange={set("website")} placeholder="https://..." inputMode="url" /></Field>
                    <Field label="Social media links"><input className="bcu-input" value={data.social_links} onChange={set("social_links")} /></Field>
                    <Field label="How long have you been operating?"><input className="bcu-input" value={data.years_operating} onChange={set("years_operating")} /></Field>
                    <Field label="Current annual revenue range"><select className="bcu-input" value={data.revenue_range} onChange={set("revenue_range")}><option value="">Select…</option><option>$0 – $10K</option><option>$10K – $50K</option><option>$50K – $150K</option><option>$150K – $500K</option><option>$500K+</option></select></Field>
                    <Field label="Inflatable units owned"><input className="bcu-input" value={data.units_owned} onChange={set("units_owned")} /></Field>
                    <Field label="Trailer or delivery vehicle available?"><select className="bcu-input" value={data.trailer_vehicle} onChange={set("trailer_vehicle")}><option value="">Select…</option><option>Yes</option><option>No</option><option>Planning to acquire</option></select></Field>
                    <Field label="Current team size"><input className="bcu-input" value={data.team_size} onChange={set("team_size")} /></Field>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#78C800] font-bold">Step 03</div>
                    <h2 className="font-display text-2xl md:text-3xl mt-1">Goals & Challenges</h2>
                  </div>
                  <p className="text-sm text-[#A9B1C1]">Tell us what "successful" looks like for you over the next twelve months.</p>
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <Field label="Your primary goals for the next 12 months" required><textarea data-testid="apply-goals" rows="4" className="bcu-input" value={data.goals_12_months} onChange={set("goals_12_months")} /></Field>
                    <Field label="Your biggest current challenge" required><textarea rows="4" className="bcu-input" value={data.biggest_challenge} onChange={set("biggest_challenge")} /></Field>
                    <Field label="Why are you interested in Bounce City University?" required><textarea rows="4" className="bcu-input" value={data.why_interested} onChange={set("why_interested")} /></Field>
                    <Field label="What would make this program successful for you?"><textarea rows="4" className="bcu-input" value={data.success_definition} onChange={set("success_definition")} /></Field>
                    <Field label="When do you want to begin?"><input className="bcu-input" value={data.start_when} onChange={set("start_when")} /></Field>
                    <Field label="How much time can you dedicate each week?"><input className="bcu-input" value={data.weekly_time} onChange={set("weekly_time")} /></Field>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Step 04</div>
                    <h2 className="font-display text-2xl md:text-3xl mt-1">Readiness</h2>
                  </div>
                  <p className="text-sm text-[#A9B1C1]">Candid questions to make sure the program matches your current commitment level.</p>
                  <div className="grid md:grid-cols-2 gap-5 md:gap-6">
                    <Field label="Are you prepared to invest $5,000 into the Bounce City University mentorship? (Current limited-time offer; regular price $10,000)" required><select data-testid="apply-invest" className="bcu-input" value={data.ready_to_invest} onChange={set("ready_to_invest")}><option>Yes</option><option>Need to discuss</option><option>No</option></select></Field>
                    <Field label="Open to discussing an approved payment arrangement?"><select className="bcu-input" value={data.open_to_payment_plan} onChange={set("open_to_payment_plan")}><option>Open to discussion</option><option>Prefer full payment</option><option>Not needed</option></select></Field>
                    <Field label="Able to attend scheduled training and coaching sessions?" required><select className="bcu-input" value={data.can_attend_training} onChange={set("can_attend_training")}><option>Yes</option><option>Need to confirm</option><option>No</option></select></Field>
                    <Field label="Willing to complete assignments?" required><select className="bcu-input" value={data.willing_to_complete} onChange={set("willing_to_complete")}><option>Yes</option><option>No</option></select></Field>
                    <Field label="Prepared for additional business costs?" hint="Equipment, insurance, software, ads, travel, licensing." required><select className="bcu-input" value={data.prepared_additional_costs} onChange={set("prepared_additional_costs")}><option>Yes</option><option>Partially</option><option>No</option></select></Field>
                    <Field label="How do you plan to fund your business launch or expansion?" hint="Do not share full credit card numbers, bank account numbers or Social Security numbers."><textarea rows="3" className="bcu-input" value={data.funding_plan} onChange={set("funding_plan")} /></Field>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6">
                  <div>
                    <div className="text-[11px] tracking-[0.22em] uppercase text-[#78C800] font-bold">Step 05</div>
                    <h2 className="font-display text-2xl md:text-3xl mt-1">Review & Consent</h2>
                  </div>
                  <p className="text-sm text-[#A9B1C1]">Please review and accept each item before submitting.</p>
                  <div className="space-y-3">
                    {[
                      { k: "consent_no_guarantee_acceptance", t: "I understand that submitting an application does not guarantee acceptance." },
                      { k: "consent_no_guarantee_results", t: "I understand that Bounce City University does not guarantee funding, revenue, profit or business success." },
                      { k: "consent_contact", t: "I consent to being contacted by phone and email regarding my application. (SMS/text-message consent is captured separately in Step 1 and is optional.)" },
                      { k: "consent_accuracy", t: "I confirm that the information provided is accurate." },
                      { k: "consent_privacy_terms", t: "I agree to the Privacy Policy and website Terms." },
                    ].map((c) => (
                      <label key={c.k} className="flex items-start gap-3 border border-[rgba(255,255,255,0.10)] p-4 rounded-lg hover:border-[#F7931E]/50 transition-colors cursor-pointer bg-[#0D0D0D]">
                        <input type="checkbox" data-testid={`apply-consent-${c.k}`} checked={!!data[c.k]} onChange={set(c.k)} className="mt-1 h-4 w-4 accent-[#F7931E]" />
                        <span className="text-sm text-[#C7CFDD]">{c.t}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-start gap-2 text-xs text-[#7A8296] pt-2">
                    <Lock className="h-3.5 w-3.5 mt-0.5 text-[#F7931E]" />
                    Applications are private. Consent, date and time are recorded with your submission.
                  </div>
                </div>
              )}

              <div className="mt-10 pt-6 border-t border-[rgba(255,255,255,0.08)] flex items-center justify-between flex-wrap gap-4">
                <button type="button" onClick={back} disabled={step === 0} data-testid="apply-back-btn" className="bcu-btn-secondary disabled:opacity-40"><ArrowLeft className="h-4 w-4" /> Back</button>
                {step < STEPS.length - 1 ? (
                  <button type="button" onClick={next} data-testid="apply-next-btn" className="bcu-btn-primary">Continue <ArrowRight className="h-4 w-4" /></button>
                ) : (
                  <button ref={submitBtnRef} type="button" onClick={submit} disabled={submitting} data-testid="apply-submit-btn" className="bcu-btn-primary">{submitting ? "Submitting…" : (<>Submit my application <Check className="h-4 w-4" /></>)}</button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT — sticky summary */}
          <aside className="lg:col-span-4 order-1 lg:order-2">
            <div className="lg:sticky lg:top-24 space-y-6">
              <div className="bcu-card bcu-card-glow-orange p-7">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Program Overview</div>
                <ul className="mt-5 space-y-4">
                  {OVERVIEW.map((o, i) => {
                    const c = i % 3 === 0 ? "text-[#F7931E]" : i % 3 === 1 ? "text-[#168CFF]" : "text-[#78C800]";
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <o.icon className={`h-5 w-5 mt-0.5 ${c} flex-shrink-0`} />
                        <div>
                          <div className="text-white font-display uppercase tracking-[0.06em]">{o.t}</div>
                          <div className="text-xs text-[#A9B1C1]">{o.s}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.10)]">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Investment</div>
                  <div className="mt-3 inline-flex items-center px-2.5 py-0.5 border border-[#F7931E]/40 bg-[#F7931E]/10 text-[#F7931E] text-[10px] tracking-[0.22em] uppercase font-semibold rounded">Limited-Time Offer</div>
                  <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A] font-semibold">Regular Investment</div>
                  <div className="font-display text-2xl text-[#7A7A7A] line-through opacity-70">$10,000</div>
                  <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">Limited-Time Enrollment</div>
                  <div className="flex items-baseline gap-2">
                    <DollarSign className="h-6 w-6 text-[#F7931E]" />
                    <div className="font-display text-4xl text-white font-semibold">5,000</div>
                  </div>
                  <p className="mt-2 text-xs text-[#A9B1C1]">One-time program investment (current offer). No enrollment payment required to submit this application.</p>
                </div>
              </div>

              <div className="bcu-card p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#168CFF] font-bold">What to expect</div>
                <ul className="mt-4 space-y-2 text-sm">
                  <li className="flex items-start gap-2 text-white"><Clock className="h-4 w-4 mt-0.5 text-[#168CFF]" />Takes 5–7 minutes</li>
                  <li className="flex items-start gap-2 text-white"><ShieldCheck className="h-4 w-4 mt-0.5 text-[#78C800]" />Applications are privately reviewed</li>
                  <li className="flex items-start gap-2 text-white"><Check className="h-4 w-4 mt-0.5 text-[#F7931E]" />Submission does not guarantee acceptance</li>
                  <li className="flex items-start gap-2 text-white"><Check className="h-4 w-4 mt-0.5 text-[#F7931E]" />Funding and results are not guaranteed</li>
                  <li className="flex items-start gap-2 text-white"><Lock className="h-4 w-4 mt-0.5 text-[#78C800]" />Applicant information is stored securely</li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
